import { Container, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend, oneformBFFYumiwiSend } from 'src/bff';
import { CodigoPostal, Country, Date, DetalleMotivo, DniNif, Email, Loading, Matricula, MultipleCTASelection, NextStepCTA, NombreApellidos, SelectDpsDomain, Telefono } from 'src/components';
import { CTAsSiNo, FormController, LayoutTemplate, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { useTrackingManager } from 'src/feature-app/hooks/use-tracking-manager';
import { formDataBuilder, formatFlags, formatHeaders } from 'src/forms/format-fields';
import { addRemovedStep, getIndexInSteps, removeStep, resetStepsScreenIndex } from 'src/forms/helpers';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { TheOriginalsConfirmacion, TheOriginalsThankYou } from './';

interface TheOriginalsFormProps {
	fullScreen: boolean;
	introTitle?: JSX.Element;
	introCopy?: JSX.Element;
  }

export function TheOriginalsForm(props: TheOriginalsFormProps) {
	const { fullScreen, introTitle, introCopy } = props;
	const { initializeForm } = useOneFormContext();
	const trackingManager = useTrackingManager();
	const store = useStore();
	const dispatch = useDispatch();
	const { formData }: OneFormState = store.getState();
	const { fields } = formData;
	const { tengoFurgo, tengoVW } = fields;
	const urlParams = new URLSearchParams(window.location.search);
	const dev = urlParams.get('dev') !== null;

	const ctaSkipTuHistoria = 'Ahora no';
	const dpsToken = 'LCV_DDB_FRM_FURGOCONCENTRACION';

	/*
	 *
	 * DATA AND SEND FORM
	 *
	 */
	const initialData: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: dpsToken,
			FORM_OBJECTIVE: 'Evento',
			FORM_DETAILS: 'The Originals 2025',
			FORM_NAME: 'adhoc-2025-the_originals',
		},
		fields: {
			formName: 'adhoc-2025-the_originals',
			formType: 'otherform',
			pageCategory: 'The Originals 2025',
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
		},
	};

	const generateData = () => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;
		const { tc, today, url, device } = getParamsForm();

		const data = {
			...formatHeaders(initialData.headers),
			'headers[SF_CAMPAIGN_NAME]': 'Web Formulario Inscripcion THOF 2025',
			//'headers[SF_CAMPAIGN_ID]': 'PENDIENTE',
			'headers[FORM_URL]': url,
			'headers[FORM_DATE_INSERTION]': today,
			'headers[FORM_NAME]': `vw-oneform-${fields.formName}`,
			'headers[FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,
			'headers[ORIGIN_DEVICE_TYPE]': device,
			'headers[ORIGIN_TRACKING_CODE]': tc,

			'fields[DPS_BRAND]': 'Volkswagen CV',
			'fields[PERSON_TREATMENT]': fields.trato,
			'fields[PERSON_NAME]': fields.name,
			'fields[PERSON_SURNAME_1]': fields.surname,
			'fields[PERSON_SURNAME_2]': fields.secondSurname,
			'fields[PERSON_EMAIL]': fields.email,
			'fields[PERSON_PHONE_MOBILE]': fields.cellphone,
			'fields[PERSON_PHONE_MOBILE_FULL]': `${fields.prefix}${fields.cellphone}`,
			//'fields[PERSON_PHONE_PREFIX]': fields.prefix,
			'fields[PERSON_DNI_NIF]': fields.dniNif,
			'fields[PERSON_BIRTHDATE]': dayjs(fields.fechaNacimiento).format('DD/MM/YYYY'),
			'fields[PERSON_POSTAL_CP]': fields.postalCode,
			'fields[EVENT_NAME]': 'The Originals 2025',
			'fields[SF_LEAD_RECORDTYPE_ID]': '0121i000000D7NyAAK',
			'fields[SF_LEAD_RECORDTYPE_NAME]': 'Sales Assistant',
			'fields[SF_LEAD_SALES_INITIAL_ACTION]': 'Prospect',

			'fields[SURVEY_ANSWER_01]': fields.tengoFurgo,
			'fields[SURVEY_ANSWER_02]': fields.tengoVW,
			'fields[SURVEY_ANSWER_03]': 'PERMITE',
			'fields[SURVEY_ANSWER_04]': fields.countryCode,
			'fields[SURVEY_ANSWER_05]': 'PERMITE',
			'fields[SURVEY_ANSWER_06]': fields.tuHistoria != ctaSkipTuHistoria ? fields.tuHistoria : '',

			'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
			'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',

			...formatFlags(initialData.flags),
		};

		if (fields.tengoFurgo == 'true') {
			if(fields.tengoVW == 'true') {
				data['fields[VEH_ACTUAL_MODEL]'] = fields.model;
			}
			else {
				data['fields[VEH_ACTUAL_BRAND]'] = fields.brand;
			}
			data['fields[VEH_ACTUAL_PLATE]'] = fields.matricula;
		}

		return data;
	};

	const sendForm = async (): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const data = generateData();
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				await sendYumiwiForm();
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const generateYumiwiData = () => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		const data = {
			'title': fields.trato == 'Sra.' ? 'ms' : 'mr',
			'name': fields.name,
			'surname': fields.surname,
			'email': fields.email,
			'phone': fields.cellphone,
			'nid': fields.dniNif,
			'birth_date': dayjs(fields.fechaNacimiento).format('YYYY-MM-DD'),
			'postal_code': fields.postalCode,
			'status': 'confirmed',
			'extra[custom#second_last_name]': fields.secondSurname,
			'extra[custom#9q6s50]': fields.countryName,
			'extra[custom#l0y4ul]': '',
			'extra[custom#iqw52l]': '',
			'extra[custom#n8m4w6]': fields.tengoFurgo == 'true' ? 'SI' : 'NO',
			'extra[custom#ssze5o]': fields.tengoVW == 'true' ? 'SI' : 'NO',
			'extra[custom#rxirdh]': fields.tengoFurgo == 'true' && fields.tengoVW == 'true' ? fields.model : '',
			'extra[custom#s3w5si]': fields.tengoFurgo == 'true' && fields.tengoVW == 'false' ? fields.brand : '',
			'extra[custom#vkb1zj]': fields.tengoFurgo == 'true' ? fields.matricula : '',
			'extra[custom#legal_lssi]': fields.lssi == 'PERMITE' ? true : false,
			'extra[custom#legal_gdpr]': fields.tmk == 'PERMITE' ? true : false,
			'extra[custom#fgq902]': true,
			'extra[custom#accept_terms_and_conditions]': true,
		};

		return data;
	};

	const sendYumiwiForm = async (): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const data = generateYumiwiData();
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFYumiwiSend(formData, '1Ec8VLmNvEvOrMZrB8MUBKsbmStfcrnVcyvA0ni4228be0ca');

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	useEffect(() => {
		if (dev) {
			console.log(generateData());
			console.log(generateYumiwiData());
			console.log(store.getState());
		}
	}, [formData]);

	/*
	 *
	 * STEPS AND INIT FORM
	 *
	 */

	let index = 0;
	const steps: Steps[] = [
		...(fullScreen
			? [{
				title: (
					<Container wrap="always">
						<Text>{introTitle}</Text>
						<Text>{introCopy}</Text>
					</Container>
				),
				fields: <LayoutTemplate element={<NextStepCTA />} />,
				screenIndex: index++,
				name: 'Introduccion',
			}] : []
		),
		{
			title: (
				<>
					Por favor, <Text bold>introduce o comprueba tus datos:</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<NombreApellidos />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'DatosPersonales',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: (
				<>
					<Text bold>¿Cuál es tu fecha de nacimiento?</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div style={{ paddingTop: '32px' }}>
							<Date name="fechaNacimiento" />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'FechaNacimiento',
		},
		{
			title: (
				<>
					Por favor, <Text bold>introduce tus datos:</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<DniNif required selectType />
							<Email required />
							<Telefono required />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'DniEmailTelefono',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', '/', 'email'],
		},
		{
			title: (
				<Container wrap="always">
					<Text>
						Cuéntanos, <Text bold>¿desde dónde vendrás a la concentración?</Text>
					</Text>
					<Text appearance={TokenTextAppearance.label200}>* Lugar de residencia actual</Text>
				</Container>
			),
			fields: (
				<LayoutTemplate
					element={
						<div style={{ paddingTop: '32px' }}>
							<CodigoPostal />
							<Country formToken={dpsToken} />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'Country',
			trueOrFalseQuestion: true,
		},
		{
			title: (
				<>
					<Text bold>¿Vienes en furgo?</Text>
				</>
			),
			fields: (
				<>
					<LayoutTemplate element={<MultipleCTASelection ctaValues={CTAsSiNo} name="tengoFurgo" ctaWidth="81" nextOnClick />} />
				</>
			),
			screenIndex: index++,
			name: 'TengoFurgo',
			trueOrFalseQuestion: true,
		},
		{
			title: (
				<>
					<Text bold>¿Es Volkswagen?</Text>
				</>
			),
			fields: (
				<>
					<LayoutTemplate element={<MultipleCTASelection ctaValues={CTAsSiNo} name="tengoVW" ctaWidth="81" nextOnClick />} />
				</>
			),
			screenIndex: index++,
			name: 'TengoVW',
			trueOrFalseQuestion: true,
		},
		{
			title: (
				<>
					<Text bold>¿Cuál es tu modelo?</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<>
							<SelectDpsDomain name="model" label="Selecciona tu modelo" token={dpsToken} domain="LCVMODELOSORIGINALFEST" nodeText="NAME" nodeValue="NAME" required />
						</>
					}
				/>
			),
			screenIndex: index++,
			name: 'Model',
		},
		{
			title: (
				<>
					<Text bold>¿Qué marca de furgo tienes?</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<>
							<SelectDpsDomain
								name="brand"
								label="Selecciona tu marca"
								token={dpsToken}
								domain="BRANDEUROTAX"
								nodeText="NAME"
								nodeValue="NAME"
								whiteList={['CITROEN', 'FIAT', 'FORD', 'MERCEDES-BENZ', 'NISSAN', 'OPEL', 'PEUGEOT', 'RENAULT', 'TOYOTA', 'HYUNDAI', 'OTRA']}
								required
							/>
						</>
					}
				/>
			),
			screenIndex: index++,
			name: 'Brand',
		},
		{
			title: (
				<>
					Por favor, <Text bold>introduce tu matrícula:</Text>
				</>
			),
			fields: <LayoutTemplate element={<Matricula />} />,
			screenIndex: index++,
			name: 'Matricula',
		},
		{
			title: (
				<>
					<Text>Sabemos que tener una furgo es algo muy especial. <Text bold>¡Queremos conocer tu historia!</Text> Cuéntanosla y si es una de las seleccionadas te contactaremos para difundirla a lo grande.</Text>
				</>
			),
			fields: <LayoutTemplate element={<DetalleMotivo required name="tuHistoria" numberOfMaxLength={300} CTALabel={ctaSkipTuHistoria} />} />,
			screenIndex: index++,
			name: 'TuHistoria',
		},
		{
			fields: <TheOriginalsConfirmacion />,
			screenIndex: index++,
			name: 'Confirmacion',
		},
	];

	useEffect(() => {

		if (tengoFurgo && tengoFurgo == 'true') {
			// Añadir en orden inverso
			updateSteps('TuHistoria', true);
			updateSteps('Matricula', true);
			updateSteps('Model', true);
			updateSteps('Brand', true);
			updateSteps('TengoVW', true);
		} else if (tengoFurgo && tengoFurgo == 'false') {
			updateSteps('TengoVW', false);
			updateSteps('Brand', false);
			updateSteps('Model', false);
			updateSteps('Matricula', false);
			updateSteps('TuHistoria', false);
		}
	}, [tengoFurgo]);

	useEffect(() => {
		if (tengoFurgo && tengoFurgo == 'true' && tengoVW && tengoVW == 'true') {
			// Añadir en orden inverso
			updateSteps('TuHistoria', true);
			updateSteps('Model', true);
			updateSteps('Brand', false);
		} else if (tengoFurgo && tengoFurgo == 'true' && tengoVW && tengoVW == 'false') {
			updateSteps('TuHistoria', false);
			updateSteps('Model', false);
			updateSteps('Brand', true);
		}
	}, [tengoVW]);

	const updateSteps = (stepName: string, conditional: boolean, addOnNextStepName?: string) => {
		const { steps: actualSteps, screenIndex } = store.getState().formInfo;
		let newSteps = [...actualSteps];
		let newScreenIndex = screenIndex;

		if (conditional) {
			const stepIndex = getIndexInSteps(newSteps, stepName);

			if (stepIndex === -1) {
				if (addOnNextStepName) {
					newScreenIndex = getIndexInSteps(newSteps, addOnNextStepName);
				}
				newSteps = addRemovedStep(steps, actualSteps, stepName, newScreenIndex);
			}
		} else {
			newSteps = removeStep(newSteps, stepName);
		}

		const newStepsWithIndex = resetStepsScreenIndex(newSteps);

		dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
		dispatch({ type: 'UPDATE_LAST_STEP', payload: newStepsWithIndex[newStepsWithIndex.length - 1] });
		dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: newStepsWithIndex.length });
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	useEffect(() => {
		const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } = getFormInfo();

		const NuevoFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			step: firstStep,
			steps: newSteps ? newSteps : steps,
			stepsHistory,
			lastStep,
			numberOfScreens,
			screenIndex: firstStep.screenIndex,
			formTheme: fullScreen ? 'main' : 'inverted',
			fullScreen: fullScreen,
			notALayer: fullScreen,
			formType: fullScreen ? 'inPage' : 'layer',
			thankYouComponent: <TheOriginalsThankYou />,
		};

		initializeForm(NuevoFormInfo, initialData, true);
		trackingManager.trackFormLoad();
	}, []);

	return !steps ? <Loading /> : <FormController steps={steps} screenType={fullScreen ? 'full-screen' : 'layer-screen'}/>;
}
